import React, { useState, useEffect } from 'react';
import {
  TextInput,
  PlusMinusContainer,
  PlusImg,
  MinusImg,
  PlusContainer,
  MinusContainer,
  HeadingAndFilterContainer,
} from './style';
import Eye from '../../../static/icons/Eye.svg';
import Plus from '../../../static/icons/Plus.svg';
import pluswhite from '../../../static/icons/pluswhite.svg';
import MarkEdit from '../../../static/img/markEdit.svg';
import EditHover from '../../../static/img/EditHover.svg';
import MinusCircle from '../../../static/img/MinusCircle.svg';
import PreviewDialog from '../../Shared/Dialogs/preview/previewCard';
import { isTabletView } from '../../ReactiveRender';
import MenuButton from '../../MenuButton';
import ImprovedSecondarySideBar from '../../ImprovedSecondarySideBar';
import ItemSettingsModal from '../../Shared/ItemSettingsModal';
import SettingsTagsDisplay from '../../Shared/SettingsTagsDisplay';
import SetupStepSection from '../../Shared/SetupStepSection';
import { useAvailableYears, useSubjects, useSubjectsForYearFromClasses, useSubjectsForYearsFromClasses, useUserStateSubjects, useTaskTypes, useResponseTypes } from '../../../hooks/useSubjects';
import { useUserStates, useMarkingToolStates } from '../../../hooks/useUserStates';
import { withSelectedSubjectsPreserved } from '../../../utils/subjects';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { visibleScrollbar } from '../../../styledMixins';
import ScrollableArea from '../../Shared/ScrollableArea';
import StepLabelBadge from '../../Shared/StepLabelBadge';

function CreateNewMarkingCriteriaDesktop(props) {
  const {
    criterias,
    addCriteria,
    addLevel,
    deleteLevel,
    addLevelInBetween,
    deleteCriteria,
    updateCriteriaTitle,
    updateLevelName,
    updateLevelDescription,
    saveMarkingCriteria,
    handleTitleChange,
    saveTitleOnly,
    deleteMarkingCriteriaMethod,
    isUpdating,
    markingCriteria,
    markingCriteriaId,
  } = props;

  const history = useHistory();
  const { states: userStates } = useUserStates();
  const state = userStates;
  const [localYears, setLocalYears] = useState(null);
  const [localSubjects, setLocalSubjects] = useState(null);
  const [localTaskTypes, setLocalTaskTypes] = useState(null);
  const [localResponseTypes, setLocalResponseTypes] = useState(null);

  const selectedYears = localYears !== null ? localYears : (markingCriteria?.years || []);
  const rawItemSubjects = markingCriteria?.metadata?.subjects || markingCriteria?.subjects || [];
  const normalizedItemSubjects = rawItemSubjects.map(s => (typeof s === 'object' && s !== null) ? (s.subjectCode || s.code || s.value || s) : s).filter(Boolean);
  const selectedSubjects = localSubjects !== null ? localSubjects : normalizedItemSubjects;
  const selectedTaskTypes = localTaskTypes !== null ? localTaskTypes : (markingCriteria?.taskTypes || (markingCriteria?.taskType ? [markingCriteria.taskType] : []));
  const selectedResponseTypes = localResponseTypes !== null ? localResponseTypes : (markingCriteria?.responseTypes || (markingCriteria?.responseType ? [markingCriteria.responseType] : []));

  const { years: availableYears } = useAvailableYears();
  const yearForFilter = selectedYears?.find((y) => availableYears?.includes(y)) ?? selectedYears?.[0];
  const yearsForFilter = Array.isArray(selectedYears) && selectedYears.length ? selectedYears : (yearForFilter != null ? [yearForFilter] : []);
  const scopeStates = useMarkingToolStates();
  const { subjects: subjectsByYear } = useSubjects(state, yearForFilter);
  const { subjects: subjectsFromClasses } = useSubjectsForYearFromClasses(yearForFilter, scopeStates);
  const { subjects: subjectsForSelectedYears } = useSubjectsForYearsFromClasses(selectedYears, scopeStates);
  const { subjects: allClassSubjects } = useUserStateSubjects(scopeStates);
  const subjectsForYear = (subjectsByYear?.length ? subjectsByYear : subjectsFromClasses) || [];
  const subjectsSource = selectedYears?.length
    ? (subjectsForSelectedYears?.length ? subjectsForSelectedYears : allClassSubjects)
    : [];
  const subjectsEnriched = (subjectsSource.length ? subjectsSource : subjectsForYear).map((s) => {
    const fromClass = (subjectsForSelectedYears?.length ? subjectsForSelectedYears : subjectsFromClasses)?.find((c) => c.code === s.code);
    return fromClass ? { ...s, taskTypes: fromClass.taskTypes || [], responseTypes: fromClass.responseTypes || [] } : s;
  });
  const { taskTypes: filteredTaskTypes } = useTaskTypes(selectedSubjects, state, yearsForFilter.length ? yearsForFilter : yearForFilter);
  const { responseTypes: filteredResponseTypes } = useResponseTypes(selectedSubjects, selectedTaskTypes, state, yearsForFilter.length ? yearsForFilter : yearForFilter);

  // Keep an edited tool's existing (possibly out-of-state) subject in the options
  // so state scoping can't silently strip it on save.
  const availableSubjects = withSelectedSubjectsPreserved(
    selectedYears?.length ? subjectsEnriched : [],
    selectedSubjects,
    rawItemSubjects,
  );
  // No subjects = no task types; no subjects or no task types = no response types
  const availableTaskTypes = selectedSubjects.length ? filteredTaskTypes : [];
  const availableResponseTypes = (selectedSubjects.length && selectedTaskTypes.length) ? filteredResponseTypes : [];

  // Auto-remove selected task types that are no longer valid
  useEffect(() => {
    if (!selectedTaskTypes.length) return;
    if (!availableTaskTypes.length) { setLocalTaskTypes([]); return; }
    const validValues = new Set(availableTaskTypes.map(t => t.value ?? t));
    const filtered = selectedTaskTypes.filter(t => validValues.has(t));
    if (filtered.length !== selectedTaskTypes.length) setLocalTaskTypes(filtered);
  }, [availableTaskTypes]);

  // Auto-remove selected response types that are no longer valid
  useEffect(() => {
    if (!selectedResponseTypes.length) return;
    if (!availableResponseTypes.length) { setLocalResponseTypes([]); return; }
    const validValues = new Set(availableResponseTypes.map(r => r.value ?? r));
    const filtered = selectedResponseTypes.filter(r => validValues.has(r));
    if (filtered.length !== selectedResponseTypes.length) setLocalResponseTypes(filtered);
  }, [availableResponseTypes]);

  const onYearsChange = (v) => {
    setLocalYears(v);
    if (!v?.length) {
      setLocalSubjects([]);
      setLocalTaskTypes([]);
      setLocalResponseTypes([]);
    }
  };
  const onSubjectsChange = (v) => {
    setLocalSubjects(v);
  };
  const onTaskTypesChange = (v) => {
    setLocalTaskTypes(v);
  };

  const [isEditing, setIsEditing] = useState(false);
  const [openMarkingCriteriaPreviewDialog, setMarkingCriteriaPreviewDialog] = useState(false);
  const [isShowMenu, setShowMenu] = React.useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const tabletView = isTabletView();
  // A Jeddle template is read-only, as in ShortAnswerDetail and the mobile
  // editor: the backend refuses (403) an edit of a rubric you don't own.
  const canEdit = !markingCriteria?.isSystem;

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      setIsEditing(false);
      if (isUpdating && saveTitleOnly) {
        saveTitleOnly(markingCriteria.title);
      }
    }
  };

  const handleOnBlur = () => {
    setIsEditing(false);
    if (isUpdating && saveTitleOnly) {
      saveTitleOnly(markingCriteria.title);
    }
  };

  const handleKeyPressInput = (e, maxLines, text) => {
    const lines = text.split('\n');
    if (e.key === 'Enter' && lines.length >= maxLines) {
      e.preventDefault();
    }
  };

  const handleYearToggle = (year) => {
    setLocalYears(selectedYears.includes(year) ? selectedYears.filter((y) => y !== year) : [...selectedYears, year]);
  };

  const handleSubjectToggle = (subject) => {
    setLocalSubjects(selectedSubjects.includes(subject) ? selectedSubjects.filter((s) => s !== subject) : [...selectedSubjects, subject]);
  };

  const removeYear = (year) => {
    setLocalYears(selectedYears.filter((y) => y !== year));
  };

  const removeSubject = (subject) => {
    setLocalSubjects(selectedSubjects.filter((s) => s !== subject));
  };

  const handleTaskTypeToggle = (type) => {
    setLocalTaskTypes(selectedTaskTypes.includes(type) ? selectedTaskTypes.filter((t) => t !== type) : [...selectedTaskTypes, type]);
  };

  const handleResponseTypeToggle = (type) => {
    setLocalResponseTypes(selectedResponseTypes.includes(type) ? selectedResponseTypes.filter((r) => r !== type) : [...selectedResponseTypes, type]);
  };

  const handleGoBack = () => {
    history.push('/tools/rubrics');
  };

  const canSave = selectedYears.length > 0 && selectedSubjects.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    saveMarkingCriteria({
      years: selectedYears,
      subjects: selectedSubjects,
      taskTypes: selectedTaskTypes,
      responseTypes: selectedResponseTypes,
    });
  };

  return (
    <>
      <MainContainer>
        <ImprovedSecondarySideBar
          isShowMenu={isShowMenu}
          setShowMenu={setShowMenu}
          id={markingCriteriaId}
        />
        <ContentWrapper>
          <InnerContainer>
            {tabletView && (
              <HeadingAndFilterContainer>
                <MenuButton setShowMenu={setShowMenu} />
              </HeadingAndFilterContainer>
            )}
            <RightContainer>
              <HeadingContainer>
                <HeadingLeft>
                  {isUpdating && (
                    <BackArrow onClick={handleGoBack}>&lt;</BackArrow>
                  )}
                  {isEditing ? (
                    <Heading id="markingCriteriaTitleContainer">
                      <TextInput
                        placeholder="Name of marking template"
                        id="markingCriteriaName"
                        value={markingCriteria.title}
                        onChange={handleTitleChange}
                        onBlur={() => handleOnBlur()}
                        onKeyPress={handleKeyPress}
                        maxLength="100"
                      />
                    </Heading>
                  ) : (
                    <Heading
                      onClick={() => canEdit && setIsEditing(true)}
                      $readOnly={!canEdit}
                    >
                      {markingCriteria.title || 'Title'}
                      {canEdit && (
                        <>
                          <EditIcon src={MarkEdit} />
                          <EditIconHover src={EditHover} />
                        </>
                      )}
                    </Heading>
                  )}
                </HeadingLeft>
                {!isUpdating && (
                  <HeaderRight>
                    <PreviewButton onClick={() => setMarkingCriteriaPreviewDialog(true)}>
                      <PreviewButtonIcon src={Eye} />
                      <PreviewButtonText>Preview</PreviewButtonText>
                    </PreviewButton>
                    <SaveButton onClick={handleSave} $disabled={!canSave}>
                      <SaveButtonText>Save and Publish</SaveButtonText>
                    </SaveButton>
                  </HeaderRight>
                )}
              </HeadingContainer>
              {isUpdating && (
                <SettingsTagsRow>
                  <SettingsTagsDisplay
                    years={markingCriteria?.years || []}
                    subjects={markingCriteria?.metadata?.subjects || markingCriteria?.subjects || []}
                    taskTypes={markingCriteria?.taskTypes || (markingCriteria?.taskType ? [markingCriteria.taskType] : [])}
                    responseTypes={markingCriteria?.responseTypes || (markingCriteria?.responseType ? [markingCriteria.responseType] : [])}
                    taskTypeLabels={Object.fromEntries((availableTaskTypes ?? []).map(t => [(t && typeof t === 'object' ? t.value : t), (t && typeof t === 'object' ? t.label ?? t.title ?? t.value : t)]))}
                    responseTypeLabels={Object.fromEntries((availableResponseTypes ?? []).map(r => [(r && typeof r === 'object' ? r.value : r), (r && typeof r === 'object' ? r.label ?? r.title ?? r.value : r)]))}
                    onSettingsClick={
                      canEdit
                        ? () => setIsSettingsOpen(!isSettingsOpen)
                        : undefined
                    }
                    showSettingsButton={canEdit}
                  />
                </SettingsTagsRow>
              )}
              {!canEdit && (
                <ReadOnlyNotice role="note">
                  This is a Jeddle template, so it’s read-only. Duplicate it
                  from the Rubrics list to make your own version.
                </ReadOnlyNotice>
              )}

              {/* Step 1 - Set Up Section (ONLY for create mode) */}
              {!isUpdating && (
                <SetupStepSection
                  availableYears={availableYears}
                  availableSubjects={availableSubjects}
                  availableTaskTypes={availableTaskTypes}
                  availableResponseTypes={availableResponseTypes}
                  selectedYears={selectedYears}
                  selectedSubjects={selectedSubjects}
                  selectedTaskTypes={selectedTaskTypes}
                  selectedResponseTypes={selectedResponseTypes}
                  onYearsChange={onYearsChange}
                  onSubjectsChange={onSubjectsChange}
                  onTaskTypesChange={onTaskTypesChange}
                  onResponseTypesChange={setLocalResponseTypes}
                  getSubjectLabel={(opt) => (typeof opt === 'object' ? opt.title : opt)}
                  getSubjectValue={(opt) => (typeof opt === 'object' ? opt.title : opt)}
                  isUpdating={isUpdating}
                />
              )}

              {/* Step 2 - Add Rubric Section (ONLY for create mode) */}
              {!isUpdating && (
                <StepLabelRow>
                  <StepLabelBadge>Step 2 - Add Rubric</StepLabelBadge>
                </StepLabelRow>
              )}

              <TableContainer>
                <TableHeadingPart>
                  <TableHeading>Criteria</TableHeading>
                  <TableHeading>Performance Levels</TableHeading>
                </TableHeadingPart>
                <BodyHeadingPart>
                  <BodyHeading></BodyHeading>
                  <BodyHeading>Level</BodyHeading>
                  <BodyHeading>Level Description</BodyHeading>
                </BodyHeadingPart>
                <TableBodyParts>
                  {markingCriteria.criterias.map(
                    (markingTemplatesRubric, templateIndex) => (
                      <TableBodyPart key={templateIndex}>
                        <CriteriaPart>
                          <TextArea
                            type="text"
                            placeholder="Answering the question"
                            value={markingTemplatesRubric.title}
                            onChange={(e) =>
                              updateCriteriaTitle(templateIndex, e.target.value)
                            }
                            maxLength="140"
                            rows="1"
                            id="criteriaName"
                            readOnly={!canEdit}
                            onKeyPress={(e) =>
                              handleKeyPressInput(
                                e,
                                1,
                                markingTemplatesRubric.title,
                              )
                            }
                          />
                        </CriteriaPart>
                        <LevelsContainer>
                          {markingTemplatesRubric.levels.map((level, index) => (
                            <LevelAndDescPart key={level.id || `level-${templateIndex}-${index}`}>
                              <LevelPart>
                                <TextArea
                                  type="text"
                                  placeholder="Level name"
                                  value={level.name}
                                  onChange={(e) =>
                                    updateLevelName(
                                      templateIndex,
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  maxLength="30"
                                  rows="1"
                                  readOnly={!canEdit}
                                  onKeyPress={(e) =>
                                    handleKeyPressInput(e, 1, level.name)
                                  }
                                />
                                {canEdit && (
                                  <PlusMinusContainer>
                                    <PlusContainer
                                      onClick={() =>
                                        addLevelInBetween(templateIndex, index)
                                      }
                                    >
                                      <PlusImg src={Plus} />
                                    </PlusContainer>
                                    <MinusContainer
                                      onClick={() =>
                                        deleteLevel(templateIndex, index)
                                      }
                                    >
                                      <MinusImg src={MinusCircle} />
                                    </MinusContainer>
                                  </PlusMinusContainer>
                                )}
                              </LevelPart>

                              <LevelDescPart>
                                <TextArea
                                  type="text"
                                  placeholder="An answer of this level should..."
                                  value={level.description}
                                  onChange={(e) =>
                                    updateLevelDescription(
                                      templateIndex,
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  maxLength="500"
                                  rows="3"
                                  readOnly={!canEdit}
                                  onKeyPress={(e) =>
                                    handleKeyPressInput(e, 3, level.description)
                                  }
                                />
                              </LevelDescPart>
                            </LevelAndDescPart>
                          ))}
                          {canEdit && (
                            <TableRowButtoncont
                              onClick={() => addLevel(templateIndex)}
                            >
                              <TableRowButton>
                                <TableRowButtonIcon src={Plus} />
                                <TableRowText>Add Level</TableRowText>
                              </TableRowButton>
                            </TableRowButtoncont>
                          )}
                        </LevelsContainer>
                      </TableBodyPart>
                    ),
                  )}
                </TableBodyParts>
              </TableContainer>
              {canEdit && (
                <AddNewCriteria>
                  <AddNewCriteriaButton onClick={addCriteria}>
                    <TableRowButtonIcon src={pluswhite}></TableRowButtonIcon>
                    <SaveButtonText>Add New Criteria</SaveButtonText>
                  </AddNewCriteriaButton>
                </AddNewCriteria>
              )}

              {/* Bottom Buttons */}
              <BottomButtonsContainer>
                <PreviewButton onClick={() => setMarkingCriteriaPreviewDialog(true)}>
                  <PreviewButtonIcon src={Eye} />
                  <PreviewButtonText>Preview</PreviewButtonText>
                </PreviewButton>
                {canEdit && (
                  <SaveButton onClick={handleSave} $disabled={!canSave}>
                    <SaveButtonText>Save and Publish</SaveButtonText>
                  </SaveButton>
                )}
              </BottomButtonsContainer>
            </RightContainer>
          </InnerContainer>

          {/* Settings Modal */}
          <ItemSettingsModal
            isOpen={isSettingsOpen}
            onClose={() => {
              setIsSettingsOpen(false);
              // Reset local overrides so next open shows item's current values
              setLocalYears(null);
              setLocalSubjects(null);
              setLocalTaskTypes(null);
              setLocalResponseTypes(null);
            }}
            onSave={(settings) => {
              saveMarkingCriteria(settings);
              setLocalYears(settings.years || []);
              setLocalSubjects(settings.subjects || []);
              setLocalTaskTypes(settings.taskTypes || []);
              setLocalResponseTypes(settings.responseTypes || []);
              setIsSettingsOpen(false);
            }}
            availableYears={availableYears}
            availableSubjects={availableSubjects}
            taskTypes={availableTaskTypes}
            responseTypes={availableResponseTypes}
            selectedYears={selectedYears}
            setSelectedYears={onYearsChange}
            selectedSubjects={selectedSubjects}
            setSelectedSubjects={onSubjectsChange}
            selectedTaskTypes={selectedTaskTypes}
            setSelectedTaskTypes={onTaskTypesChange}
            selectedResponseTypes={selectedResponseTypes}
            setSelectedResponseTypes={setLocalResponseTypes}
          />
        </ContentWrapper>
      </MainContainer>

      {openMarkingCriteriaPreviewDialog && (
        <PreviewDialog
          setMarkingCriteriaPreviewDialog={setMarkingCriteriaPreviewDialog}
          markingCriterias={{ ...markingCriteria, type: 'RUBRICS' }}
        />
      )}
    </>
  );
}

// Styled Components
const MainContainer = styled.div`
  width: 100%;
  position: relative;
  align-items: flex-start;
  background-color: var(--white);
  display: flex;
  flex-direction: row;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100vh;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  position: relative;
`;

const RightContainer = styled(ScrollableArea).attrs({ autoHide: 'leave' })`
  width: 100%;
  background: #ffffff;
  height: 100vh;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #c9c6cc80;
  background: #ffffff;
`;

const HeadingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

const BackArrow = styled.span`
  font-family: IBM Plex Sans;
  font-size: 20px;
  font-weight: 400;
  color: #7200e0;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    opacity: 0.9;
  }
`;

const Heading = styled.p`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: -0.01em;
  text-align: left;
  display: flex;
  align-items: center;
  padding: 8px 6px;
  gap: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  margin: 0;
  flex: 1;
  min-width: 0;

  &:hover {
    border: 1px solid #c9c6cc80;
  }

  ${({ $readOnly }) =>
    $readOnly &&
    `
    cursor: default;

    &:hover {
      border: 1px solid transparent;
    }
  `}
`;

const EditIcon = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.6;
`;

const EditIconHover = styled.img`
  width: 20px;
  height: 20px;
  display: none;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SettingsTagsRow = styled.div`
  padding: 12px 20px 16px;
  border-bottom: 1px solid #c9c6cc80;
  background: #FBF7FE;
`;

const ReadOnlyNotice = styled.p`
  margin: 0;
  padding: 12px 20px;
  border-bottom: 1px solid #c9c6cc80;
  background: #ffffff;
  font-family: IBM Plex Sans;
  font-size: 14px;
  line-height: 20px;
  color: #56515b;
`;


const PreviewButton = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 32px;
  border: 1px solid #7200e0;
  gap: 4px;
  background: #ffffff;
  cursor: pointer;
  height: 40px;

  &:hover {
    background-color: #f9f5ff;
  }
`;

const PreviewButtonIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const PreviewButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #7200e0;
  margin: 0;
`;

const SaveButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  border-radius: 32px;
  background: #7200e0;
  cursor: pointer;

  &:hover {
    background-color: rgba(80, 0, 157, 1);
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    background: #c9c6cc;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const SaveButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #ffffff;
  margin: 0;
`;

// Step Section Styles
const StepLabelRow = styled.div`
  padding: 16px 20px;
  background: #ffffff;
`;

// Table Styles
const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const TableHeadingPart = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1.5fr 3.5fr;
`;

const TableHeading = styled.div`
  padding: 16px 20px;
  background: #f8f8f8;
  border: 1px solid #e5e5e5;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 600;
  color: #1e252a;
`;

const BodyHeadingPart = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1.5fr 0.7fr 2.8fr;
`;

const BodyHeading = styled.div`
  padding: 8px 20px;
  border: 1px solid #e5e5e5;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 600;
  background: #faf8fc;
  color: #56515b;
`;

const TableBodyParts = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TableBodyPart = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1.5fr 3.5fr;
  border-bottom: 1px solid #e5e5e5;
`;

const CriteriaPart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  border-right: 1px solid #e5e5e5;
  background: #ffffff;
`;

const LevelsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LevelPart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  border-right: 1px solid #e5e5e5;
`;

const LevelAndDescPart = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 0.7fr 2.8fr;
  border-bottom: 1px solid #e5e5e5;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    & > ${LevelPart} > div {
      display: flex;
    }
  }
`;

const LevelDescPart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 100%;
  min-height: 60px;
  resize: none;
  box-sizing: border-box;
  padding: 12px 16px;
  border: none;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 400;
  color: #1e252a;
  outline: none;
  cursor: text;
  background: #ffffff;

  &:focus::placeholder {
    color: transparent;
  }

  &:hover {
    background-color: #fafafa;
  }

  &:focus {
    background-color: #f5f5f5;
  }

  &:read-only {
    background-color: #ffffff;
    cursor: default;
  }

  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

const TableRowButtoncont = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding: 12px 16px;
  cursor: pointer;
  background: #ffffff;
  border-top: 1px solid #e5e5e5;

  &:hover {
    background: #fafafa;
  }
`;

const TableRowButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const TableRowButtonIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const TableRowText = styled.div`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  color: #7200e0;
`;

const AddNewCriteria = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 20px;
  gap: 10px;
  background: #ffffff;
`;

const AddNewCriteriaButton = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 32px;
  gap: 4px;
  background: #7200e0;
  cursor: pointer;

  &:hover {
    background: rgba(80, 0, 157, 1);
  }
`;

const BottomButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: #ffffff;
  border-top: 1px solid #e5e5e5;
`;

export default CreateNewMarkingCriteriaDesktop;
